import {
  Avatar,
  Badge,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  Paper,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';

import {
  Dashboard as DashboardIcon,
  Healing as HealingIcon,
  People as PeopleIcon,
  Speed as SpeedIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';
import TransferWithinAStationIcon from '@mui/icons-material/TransferWithinAStation';
import { useDispatch, useSelector } from 'react-redux';
import ReusableTable from '../common/table/CommonTable';
import { fetchStaffCountRequest } from '../redux/dashboard/dashboardAction';
import {
  fetchHistoryPatients,
  fetchPatients,
} from '../redux/patient/patientAction';
import { fetchSecurityOfficers } from '../redux/secuityOfficer/securityAction';
import { fetchTransferPatient } from '../redux/TransferPatient/transferPatientAction';

// Animated components using framer-motion
const MotionCard = motion(Card);
const MotionTypography = motion(Typography);
const MotionBox = motion(Box);
const MotionPaper = motion(Paper);
const MotionAvatar = motion(Avatar);

const StaffDashboard = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processedData, setProcessedData] = useState([]);

  const securityOfficerState = useSelector(
    (state) => state?.securityOfficer?.fetchSecurityOfficers || {}
  );
  const securityOfficerList = securityOfficerState?.data?.data || [];

  const patientList = useSelector(
    (state) => state?.patients?.fetchListPatients || {}
  );
  const adminState = useSelector(
    (state) => state?.dashboard?.fetchStaffCount?.count || {}
  );
  const columnSecurity = [
    { id: 'security_serial_no', label: 'Guard ID', sortable: true },
    { id: 'security_gaurd_name', label: 'Name', sortable: true },
    { id: 'email', label: 'Email', sortable: true },
    { id: 'phone_number', label: 'Phone Number', sortable: true },
    // { id: 'blockName', label: 'Block', sortable: true },
    // { id: 'floorName', label: 'Floor', sortable: true },
    // { id: 'wardName', label: 'Ward', sortable: true },
    {
      id: 'status',
      label: 'Status',
      sortable: true,
      render: (value) => (
        <Chip
          label={value === 1 ? 'Active' : 'Inactive'}
          color={value === 1 ? 'success' : 'warning'}
        />
      ),
    },
  ];
  useEffect(() => {
    const fetchData = async () => {
      await dispatch(fetchPatients());
      await dispatch(fetchStaffCountRequest());
      await dispatch(fetchSecurityOfficers());
      setLoading(false);
    };
    fetchData();
  }, [dispatch]);

  useEffect(() => {
    if (patientList.data && Array.isArray(patientList.data.data)) {
      const formattedData = patientList.data.data.map((patient) => ({
        id: patient.patient_serial_no,
        name: patient.patient_name,
        age: patient.age,
        status: patient.status || 'Active',
      }));
      setData(formattedData);
    } else {
      setData([]);
    }
  }, [patientList]);

  const [currentPage, setCurrentPage] = useState(1);
  useEffect(() => {
    setLoading(true);
    dispatch(fetchHistoryPatients(currentPage));
  }, [dispatch, currentPage]);

  const historyState = useSelector(
    (state) => state?.patients?.historyPatient || {}
  );
  const historyData = historyState?.data?.data || [];
  const [filteredData, setFilteredData] = useState([]);
  const transferState = useSelector(
    (state) => state?.transferPatient?.fetchListTransferPatient || {}
  );
  useEffect(() => {
    if (historyData.length > 0) {
      const processedData = historyData.map((item) => ({
        attendee: item.Attender?.attender_name || 'Unknown',
        attender_serial_no: item.Attender?.attender_serial_no || 'Unknown',
        status: item.attender_status === 2 ? 'In' : 'Out',
      }));

      setFilteredData(processedData);
      setLoading(false);
    }
  }, [historyData]);

  const statsColors = {
    patients: '#4a47a3',
    attendees: '#1e988a',
    events: '#e63946',
    scans: '#fb8b24',
    iconHover: '#457b9d',
    progress: '#4cc9f0',
    cardBg1: 'linear-gradient(135deg, #f6f9fc 0%, #eef2f6 100%)',
    cardBg2: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
    headerBg1: 'linear-gradient(135deg, #4a47a3 0%, #6a67c3 100%)',
    headerBg2: 'linear-gradient(135deg, #1e988a 0%, #3eb8aa 100%)',
    headerBg3: 'linear-gradient(135deg, #e63946 0%, #f07981 100%)',
    headerBg4: 'linear-gradient(135deg, #fb8b24 0%, #fca954 100%)',
    tableBg1: 'linear-gradient(135deg, #3f51b5 0%, #5c6bc0 100%)',
    tableBg2: 'linear-gradient(135deg, #009688 0%, #26a69a 100%)',
    tableBg3: 'linear-gradient(135deg, #f57c00 0%, #ff9800 100%)',
  };

  useEffect(() => {
    dispatch(fetchTransferPatient());
  }, [dispatch]);

  useEffect(() => {
    if (Array.isArray(transferState?.data?.data)) {
      const processed = transferState.data.data.map((item, index) => ({
        serialNo: index + 1,
        id: item.id,
        patientName: item.Patient?.patient_name || 'N/A',
        ipNumber: item.Patient?.ip_number || 'N/A',
        phoneNumber: item.Patient?.phone || 'N/A',
        staffName: item.Staff?.staff_name || 'N/A',
        oldBlock: item.old_Block?.block_name || 'N/A',
        oldFloor: item.old_Floor?.floor_name || 'N/A',
        oldWard: item.old_Ward?.ward_name || 'N/A',
        newBlock: item.new_Block?.block_name || 'N/A',
        newFloor: item.new_Floor?.floor_name || 'N/A',
        newWard: item.new_Ward?.ward_name || 'N/A',
        transferDate: new Date(item.createdAt).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        }),
        reason: item.reason || 'N/A',
      }));
      setProcessedData(processed);
      setFilteredData(processed);
    } else {
      setProcessedData([]);
      setFilteredData([]);
    }
  }, [transferState]);

  const columns = [
    { id: 'serialNo', label: 'S.No', sortable: true },
    { id: 'patientName', label: 'Patient Name', sortable: true },
    { id: 'phoneNumber', label: 'Phone Number', sortable: true },
    {
      id: 'oldBlock',
      label: 'Old Block',
      sortable: true,
      render: (value) => (
        <Chip
          label={value}
          color="warning"
          size="small"
          sx={{
            fontWeight: 'bold',
            background: 'linear-gradient(45deg, #ff9800 30%, #ffb74d 90%)',
            color: 'white',
            boxShadow: '0 2px 5px rgba(255, 152, 0, 0.3)',
          }}
        />
      ),
    },
    {
      id: 'oldFloor',
      label: 'Old Floor',
      sortable: true,
      render: (value) => (
        <Chip
          label={value}
          color="warning"
          size="small"
          sx={{
            fontWeight: 'bold',
            background: 'linear-gradient(45deg, #ff9800 30%, #ffb74d 90%)',
            color: 'white',
            boxShadow: '0 2px 5px rgba(255, 152, 0, 0.3)',
          }}
        />
      ),
    },
    {
      id: 'oldWard',
      label: 'Old Ward',
      sortable: true,
      render: (value) => (
        <Chip
          label={value}
          color="warning"
          size="small"
          sx={{
            fontWeight: 'bold',
            background: 'linear-gradient(45deg, #ff9800 30%, #ffb74d 90%)',
            color: 'white',
            boxShadow: '0 2px 5px rgba(255, 152, 0, 0.3)',
          }}
        />
      ),
    },
    {
      id: 'newBlock',
      label: 'New Block',
      sortable: true,
      render: (value) => (
        <Chip
          label={value}
          color="success"
          size="small"
          sx={{
            fontWeight: 'bold',
            background: 'linear-gradient(45deg, #4caf50 30%, #81c784 90%)',
            color: 'white',
            boxShadow: '0 2px 5px rgba(76, 175, 80, 0.3)',
          }}
        />
      ),
    },
    {
      id: 'newFloor',
      label: 'New Floor',
      sortable: true,
      render: (value) => (
        <Chip
          label={value}
          color="success"
          size="small"
          sx={{
            fontWeight: 'bold',
            background: 'linear-gradient(45deg, #4caf50 30%, #81c784 90%)',
            color: 'white',
            boxShadow: '0 2px 5px rgba(76, 175, 80, 0.3)',
          }}
        />
      ),
    },
    {
      id: 'newWard',
      label: 'New Ward',
      sortable: true,
      render: (value) => (
        <Chip
          label={value}
          color="success"
          size="small"
          sx={{
            fontWeight: 'bold',
            background: 'linear-gradient(45deg, #4caf50 30%, #81c784 90%)',
            color: 'white',
            boxShadow: '0 2px 5px rgba(76, 175, 80, 0.3)',
          }}
        />
      ),
    },
    {
      id: 'transferDate',
      label: 'Transfer Date',
      sortable: true,
      render: (value) => (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 'medium',
            color: '#2c3e50',
          }}
        >
          {value}
        </Typography>
      ),
    },
  ];
  

  const statistics = {
    totalPatients: adminState?.count?.admissionCount || 0,
    activeAttendees: adminState?.count?.patientShift || 0,
    attendeesIn: adminState?.count?.inAttenderCount || 0,
    attendeesOut: adminState?.count?.outAttenderCount || 0,
    scanToday: adminState?.count?.patientShift || 0,
  };

  const patientColumns = [
    { id: 'id', label: 'ID', sortable: true },
    {
      id: 'name',
      label: 'Name',
      sortable: true,
      render: (value) => (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 'bold',
            color: '#2c3e50',
          }}
        >
          {value}
        </Typography>
      ),
    },
    { id: 'age', label: 'DOB', sortable: false },
    {
      id: 'status',
      label: 'Status',
      sortable: true,
      render: (status) => (
        <Box
          sx={{
            background:
              status === 'IN'
                ? 'linear-gradient(45deg, #4caf50 30%, #81c784 90%)'
                : 'linear-gradient(45deg, #f44336 30%, #e57373 90%)',
            borderRadius: 2,
            px: 1.5,
            py: 0.5,
            display: 'inline-block',
            color: 'white',
            fontWeight: 'bold',
            boxShadow:
              status === 'IN'
                ? '0 2px 5px rgba(76, 175, 80, 0.3)'
                : '0 2px 5px rgba(244, 67, 54, 0.3)',
          }}
        >
          {status}
        </Box>
      ),
    },
  ];

  const attendeeColumns = [
    {
      id: 'attendee',
      label: 'Attendee Name',
      sortable: true,
      render: (value) => (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 'bold',
            color: '#2c3e50',
          }}
        >
          {value}
        </Typography>
      ),
    },
    { id: 'attender_serial_no', label: 'Attendee Serial No', sortable: true },
    {
      id: 'status',
      label: 'Status',
      sortable: true,
      render: (status) => (
        <Box
          sx={{
            background:
              status === 'In'
                ? 'linear-gradient(45deg, #4caf50 30%, #81c784 90%)'
                : 'linear-gradient(45deg, #f44336 30%, #e57373 90%)',
            borderRadius: 2,
            px: 1.5,
            py: 0.5,
            display: 'inline-block',
            color: 'white',
            fontWeight: 'bold',
            boxShadow:
              status === 'In'
                ? '0 2px 5px rgba(76, 175, 80, 0.3)'
                : '0 2px 5px rgba(244, 67, 54, 0.3)',
          }}
        >
          {status}
        </Box>
      ),
    },
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        duration: 0.5,
      },
    },
  };

  const countVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        delay: 0.3,
        duration: 0.8,
      },
    },
  };

  const calculatePercentage = (value, max) => {
    return max > 0 ? (value / max) * 100 : 0;
  };

  const renderProgress = (value, total, color) => {
    const percentage = calculatePercentage(value, total);
    return (
      <Box sx={{ width: '100%', mt: 1 }}>
        <Box display="flex" justifyContent="space-between" mb={0.5}>
          <Typography variant="caption" color="text.secondary">
            {value} / {total}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {percentage.toFixed(0)}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={percentage}
          sx={{
            height: 8,
            borderRadius: 4,
            backgroundColor: 'rgba(0,0,0,0.09)',
            '& .MuiLinearProgress-bar': {
              background: color,
              borderRadius: 4,
            },
          }}
        />
      </Box>
    );
  };

  return (
    <MotionBox
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      sx={{
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%)',
        minHeight: '100vh',
        pb: 4,
      }}
    >
      {/* Main Content */}
      <Box
        component="main"
        sx={{
          p: { xs: 1, sm: 2, md: 3 },
          width: '100%',
        }}
      >
        {/* Count Boxes */}
        <MotionBox
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          component={Grid}
          container
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Grid item xs={6} md={3}>
            <MotionCard
              variants={itemVariants}
              whileHover={{
                scale: 1.03,
                boxShadow: '0 10px 30px rgba(74, 71, 163, 0.2)',
              }}
              whileTap={{ scale: 0.98 }}
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                height: '100%',
                background: 'white',
                boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
              }}
            >
              <Box
                sx={{
                  background: statsColors.headerBg1,
                  pt: 2,
                  pb: 2,
                  px: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography
                  color="white"
                  variant="subtitle1"
                  fontWeight="bold"
                  fontSize={isMobile ? '0.8rem' : '1rem'}
                >
                  Currently Registered
                </Typography>
                <MotionBox
                  whileHover={{ rotate: 15, scale: 1.2 }}
                  transition={{ type: 'spring', stiffness: 400 }}
                >
                  <Avatar
                    sx={{
                      bgcolor: 'white',
                      color: statsColors.patients,
                      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                    }}
                  >
                    <DashboardIcon />
                  </Avatar>
                </MotionBox>
              </Box>
              <CardContent sx={{ py: 3 }}>
                <MotionTypography
                  variants={countVariants}
                  variant="h3"
                  align="center"
                  color={statsColors.patients}
                  sx={{
                    fontSize: isMobile ? '1.8rem' : '2.5rem',
                    fontWeight: 'bold',
                    mb: 1,
                  }}
                >
                  {statistics.totalPatients}
                </MotionTypography>
                {renderProgress(
                  statistics.totalPatients,
                  200,
                  statsColors.headerBg1
                )}
              </CardContent>
            </MotionCard>
          </Grid>

          <Grid item xs={6} md={3}>
            <MotionCard
              variants={itemVariants}
              whileHover={{
                scale: 1.03,
                boxShadow: '0 10px 30px rgba(30, 152, 138, 0.2)',
              }}
              whileTap={{ scale: 0.98 }}
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                height: '100%',
                background: 'white',
                boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
              }}
            >
              <Box
                sx={{
                  background: statsColors.headerBg2,
                  pt: 2,
                  pb: 2,
                  px: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography
                  color="white"
                  variant="subtitle1"
                  fontWeight="bold"
                  fontSize={isMobile ? '0.8rem' : '1rem'}
                >
                  Active Attendees IN
                </Typography>
                <MotionBox
                  whileHover={{ rotate: 15, scale: 1.2 }}
                  transition={{ type: 'spring', stiffness: 400 }}
                >
                  <Badge
                    badgeContent="IN"
                    color="success"
                    sx={{
                      '& .MuiBadge-badge': {
                        fontWeight: 'bold',
                        fontSize: '0.6rem',
                      },
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: 'white',
                        color: statsColors.attendees,
                        boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                      }}
                    >
                      <PeopleIcon />
                    </Avatar>
                  </Badge>
                </MotionBox>
              </Box>
              <CardContent sx={{ py: 3 }}>
                <MotionTypography
                  variants={countVariants}
                  variant="h3"
                  align="center"
                  color={statsColors.attendees}
                  sx={{
                    fontSize: isMobile ? '1.8rem' : '2.5rem',
                    fontWeight: 'bold',
                    mb: 1,
                  }}
                >
                  {statistics.attendeesIn}
                </MotionTypography>
                {renderProgress(
                  statistics.attendeesIn,
                  statistics.attendeesIn + statistics.attendeesOut,
                  statsColors.headerBg2
                )}
              </CardContent>
            </MotionCard>
          </Grid>

          <Grid item xs={6} md={3}>
            <MotionCard
              variants={itemVariants}
              whileHover={{
                scale: 1.03,
                boxShadow: '0 10px 30px rgba(230, 57, 70, 0.2)',
              }}
              whileTap={{ scale: 0.98 }}
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                height: '100%',
                background: 'white',
                boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
              }}
            >
              <Box
                sx={{
                  background: statsColors.headerBg3,
                  pt: 2,
                  pb: 2,
                  px: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography
                  color="white"
                  variant="subtitle1"
                  fontWeight="bold"
                  fontSize={isMobile ? '0.8rem' : '1rem'}
                >
                  Active Attendees OUT
                </Typography>
                <MotionBox
                  whileHover={{ rotate: 15, scale: 1.2 }}
                  transition={{ type: 'spring', stiffness: 400 }}
                >
                  <Badge
                    badgeContent="OUT"
                    color="error"
                    sx={{
                      '& .MuiBadge-badge': {
                        fontWeight: 'bold',
                        fontSize: '0.6rem',
                      },
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: 'white',
                        color: statsColors.events,
                        boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                      }}
                    >
                      <PeopleIcon />
                    </Avatar>
                  </Badge>
                </MotionBox>
              </Box>
              <CardContent sx={{ py: 3 }}>
                <MotionTypography
                  variants={countVariants}
                  variant="h3"
                  align="center"
                  color={statsColors.events}
                  sx={{
                    fontSize: isMobile ? '1.8rem' : '2.5rem',
                    fontWeight: 'bold',
                    mb: 1,
                  }}
                >
                  {statistics.attendeesOut}
                </MotionTypography>
                {renderProgress(
                  statistics.attendeesOut,
                  statistics.attendeesIn + statistics.attendeesOut,
                  statsColors.headerBg3
                )}
              </CardContent>
            </MotionCard>
          </Grid>

          <Grid item xs={6} md={3}>
            <MotionCard
              variants={itemVariants}
              whileHover={{
                scale: 1.03,
                boxShadow: '0 10px 30px rgba(251, 139, 36, 0.2)',
              }}
              whileTap={{ scale: 0.98 }}
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                height: '100%',
                background: 'white',
                boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
              }}
            >
              <Box
                sx={{
                  background: statsColors.headerBg4,
                  pt: 2,
                  pb: 2,
                  px: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography
                  color="white"
                  variant="subtitle1"
                  fontWeight="bold"
                  fontSize={isMobile ? '0.8rem' : '1rem'}
                >
                  Transfers Today
                </Typography>
                <MotionBox
                  whileHover={{ rotate: 15, scale: 1.2 }}
                  transition={{ type: 'spring', stiffness: 400 }}
                >
                  <Avatar
                    sx={{
                      bgcolor: 'white',
                      color: statsColors.scans,
                      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                    }}
                  >
                    <TransferWithinAStationIcon />
                  </Avatar>
                </MotionBox>
              </Box>
              <CardContent sx={{ py: 3 }}>
                <MotionTypography
                  variants={countVariants}
                  variant="h3"
                  align="center"
                  color={statsColors.scans}
                  sx={{
                    fontSize: isMobile ? '1.8rem' : '2.5rem',
                    fontWeight: 'bold',
                    mb: 1,
                  }}
                >
                  {statistics.scanToday}
                </MotionTypography>
                {renderProgress(
                  statistics.scanToday,
                  50,
                  statsColors.headerBg4
                )}
              </CardContent>
            </MotionCard>
          </Grid>
        </MotionBox>

        {/* Status Overview Bar */}
        <MotionBox
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          sx={{ mb: 3 }}
        >
          <Card
            sx={{
              borderRadius: 3,
              p: 2,
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
              boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <SpeedIcon sx={{ color: statsColors.patients, mr: 1 }} />
                  <Typography
                    variant="h6"
                    color="textPrimary"
                    sx={{ fontWeight: 'medium' }}
                  >
                    System Status
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={9}>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Occupancy Rate
                      </Typography>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}
                      >
                        <LinearProgress
                          variant="determinate"
                          value={75}
                          sx={{
                            flexGrow: 1,
                            height: 8,
                            borderRadius: 4,
                            mr: 1,
                            backgroundColor: 'rgba(0,0,0,0.09)',
                            '& .MuiLinearProgress-bar': {
                              background:
                                'linear-gradient(45deg, #4a47a3 30%, #6759ff 90%)',
                              borderRadius: 4,
                            },
                          }}
                        />
                        <Typography variant="body2" fontWeight="bold">
                          75%
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Attendees Coverage
                      </Typography>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}
                      >
                        <LinearProgress
                          variant="determinate"
                          value={88}
                          sx={{
                            flexGrow: 1,
                            height: 8,
                            borderRadius: 4,
                            mr: 1,
                            backgroundColor: 'rgba(0,0,0,0.09)',
                            '& .MuiLinearProgress-bar': {
                              background:
                                'linear-gradient(45deg, #1e988a 30%, #3eb8aa 90%)',
                              borderRadius: 4,
                            },
                          }}
                        />
                        <Typography variant="body2" fontWeight="bold">
                          88%
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        System Load
                      </Typography>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}
                      >
                        <LinearProgress
                          variant="determinate"
                          value={42}
                          sx={{
                            flexGrow: 1,
                            height: 8,
                            borderRadius: 4,
                            mr: 1,
                            backgroundColor: 'rgba(0,0,0,0.09)',
                            '& .MuiLinearProgress-bar': {
                              background:
                                'linear-gradient(45deg, #fb8b24 30%, #fca954 90%)',
                              borderRadius: 4,
                            },
                          }}
                        />
                        <Typography variant="body2" fontWeight="bold">
                          42%
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Card>
        </MotionBox>

        {/* Recent Patients and Current Attendees using ReusableTable */}
        <MotionBox
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          component={Grid}
          container
          spacing={2}
          sx={{ mb: 3 }}
        >
          {/* Recent Patients */}
          <Grid item xs={12} md={6}>
            <MotionCard
              whileHover={{
                y: -5,
                boxShadow: '0 10px 30px rgba(63, 81, 181, 0.2)',
              }}
              transition={{ type: 'spring', stiffness: 300 }}
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                '& .MuiCardHeader-root': {
                  background: statsColors.tableBg1,
                  color: 'white',
                },
              }}
            >
              <ReusableTable
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <HealingIcon sx={{ mr: 1 }} />
                    <Typography fontWeight="bold">Recent Patients</Typography>
                  </Box>
                }
                columns={patientColumns}
                data={data.slice(0, 5)}
                headerColor={"#000"}
                withPagination={false}
                withActions={false}
                rowsPerPage={4}
                customEmptyMessage="No patients registered"
                tableProps={{
                  sx: {
                    '& .MuiTableRow-root:hover': {
                      backgroundColor: 'rgba(63, 81, 181, 0.05)',
                    },
                    '& .MuiTableCell-head': {
                      fontWeight: 'bold',
                    },
                  },
                }}
              />
            </MotionCard>
          </Grid>

          {/* Current Attendees */}
          <Grid item xs={12} md={6}>
            <MotionCard
              whileHover={{
                y: -5,
                boxShadow: '0 10px 30px rgba(0, 150, 136, 0.2)',
              }}
              transition={{ type: 'spring', stiffness: 300 }}
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                '& .MuiCardHeader-root': {
                  background: statsColors.tableBg2,
                  color: 'white',
                },
              }}
            >
              <ReusableTable
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PeopleIcon sx={{ mr: 1 }} />
                    <Typography fontWeight="bold">Current Security</Typography>
                  </Box>
                }
                columns={columnSecurity}
                data={securityOfficerList.slice(0, 5)}
                headerColor={"#000"}
                withPagination={false}
                withActions={false}
                rowsPerPage={4}
                customEmptyMessage="No attendees registered"
                tableProps={{
                  sx: {
                    '& .MuiTableRow-root:hover': {
                      backgroundColor: 'rgba(0, 150, 136, 0.05)',
                    },
                    '& .MuiTableCell-head': {
                      fontWeight: 'bold',
                    },
                  },
                }}
              />
            </MotionCard>
          </Grid>
        </MotionBox>

        {/* Recent Activity */}
        <MotionBox
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          component={Grid}
          container
          spacing={2}
        >
          <Grid item xs={12}>
            <MotionCard
              whileHover={{
                y: -5,
                boxShadow: '0 10px 30px rgba(245, 124, 0, 0.2)',
              }}
              transition={{ type: 'spring', stiffness: 300 }}
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                '& .MuiCardHeader-root': {
                  background: statsColors.tableBg3,
                  color: 'white',
                },
              }}
            >
              <ReusableTable
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <TimelineIcon sx={{ mr: 1 }} />
                    <Typography fontWeight="bold">
                      Patient Transfer Activity
                    </Typography>
                  </Box>
                }
                columns={columns}
                data={processedData.slice(0, 5)}
                headerColor={"#000"}
                withPagination={true}
                withActions={false}
                rowsPerPage={5}
                customEmptyMessage="No recent activity"
                tableProps={{
                  sx: {
                    '& .MuiTableRow-root:hover': {
                      backgroundColor: 'rgba(245, 124, 0, 0.05)',
                    },
                    '& .MuiTableCell-head': {
                      fontWeight: 'bold',
                    },
                  },
                }}
              />
            </MotionCard>
          </Grid>
        </MotionBox>

        {/* Footer Stats */}
        <MotionBox
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.8 }}
          sx={{ mt: 3 }}
        >
          <Card
            sx={{
              p: 2,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
              boxShadow: '0 8px 16px rgba(0,0,0,0.05)',
            }}
          >
            <Grid
              container
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
            >
              <Grid item xs={12} md={8}>
                <Typography variant="body2" color="textSecondary">
                  System statistics updated as of:{' '}
                  <b>{new Date().toLocaleString()}</b>
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="caption" color="textSecondary" mr={1}>
                    Data refresh rate: 5 minutes
                  </Typography>
                  <Chip
                    label="Live"
                    size="small"
                    sx={{
                      background:
                        'linear-gradient(45deg, #4caf50 30%, #81c784 90%)',
                      color: 'white',
                      fontWeight: 'bold',
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Card>
        </MotionBox>
      </Box>
    </MotionBox>
  );
};

export default StaffDashboard;
